import { lazy, useState, useEffect, Suspense, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Items from './SearchResult/Items'
import OrderBy from './SearchResult/OrderBy'
import Pagination from './SearchResult/Pagination'
import { itemsFetchData } from '../redux/items'
import ChangeCurrency from './SearchResult/ChangeCurrency'
import FooterDescription from './SearchFooter/FooterDescription'
import FaqContainer from './SearchFaq/FaqContainer'
import EmbeddedVideo from './EmbeddedVideo'
import { ChangeView } from './SearchResult/ChangeView'
import { InternalLinks } from './SearchResult/InternalLinks'
import { parseSubjectsFromUrl } from '../SearchUtils'
import { detectBrowserCoordinates } from '../../lib/detectBrowserCoordinates'
import { TotalResults } from './TotalResults'
import { LoginModalContext } from '../../Login/context/LoginModalContext'

const AddToGarageModal = lazy(() =>
  import('../../components/AddToGarage/AddToGarageModal')
)

const SearchResult = () => {
  const items = useSelector((state) => state.items)
  const { openLoginModal } = useContext(LoginModalContext)

  const dispatch = useDispatch()

  const [selectedViewOption, setSelectedViewOption] = useState('grid')
  const [garageModalIsOpen, setGarageModalIsOpen] = useState(false)
  const [selectedCarId, setSelectedCarId] = useState(null)

  useEffect(() => {
    let viewOption

    if (localStorage) {
      viewOption = localStorage.getItem('view_option')

      if (!['grid', 'list'].includes(viewOption)) {
        viewOption = 'grid'
        localStorage.setItem('view_option', 'grid')
      }
    }

    if (viewOption) setSelectedViewOption(viewOption)
  }, [])

  const setLocation = (e) => {
    e.preventDefault()

    const { currentFilters } = items

    const newFilters = {
      popstate: 0,
      subjects: parseSubjectsFromUrl(),
      ...currentFilters,
    }

    detectBrowserCoordinates(
      () => {
        dispatch(itemsFetchData({ filters: newFilters }))
      },
      () => {
        dispatch(itemsFetchData({ filters: newFilters }))
      }
    )
  }

  const handleViewChange = (view) => {
    localStorage.setItem('view_option', view)
    setSelectedViewOption(view)
    App.GAEvent('Cars Search', `Change View Option (${view})`, null, null)
  }

  const handleOpenGarageModal = (e, carId) => {
    e.stopPropagation()
    e.preventDefault()

    App.GAEvent('Cars Search', 'Click Add To Garage', null, null)

    if (App.State.userSignedIn) {
      setSelectedCarId(carId)
      setGarageModalIsOpen(true)
    } else {
      openLoginModal()
    }
  }

  const handleCloseGarageModal = () => {
    setGarageModalIsOpen(false)
  }

  return (
    <>
      <div className="content explore-cars mb-3" id="search-result">
        <TotalResults className="d-sm-none" />
        <div className="d-flex align-items-center">
          <TotalResults className="d-none d-sm-block me-4" />
          <OrderBy />
          <ChangeCurrency />
          <ChangeView
            handleViewChange={handleViewChange}
            viewOption={selectedViewOption}
          />
        </div>
        <div className="col-12 d-flex justify-content-between align-items-center">
          {items.currentFilters && items.currentFilters.sort === 'distance' && (
            <a href="#" onClick={setLocation}>
              <i className="fas fa-map-marker-alt text-danger" />{' '}
              {I18n.t('refresh_location')}
            </a>
          )}
        </div>

        <Items
          items={items.data}
          premiumCars={items.premiumCars}
          viewOption={selectedViewOption}
          currency={items.currency}
          modalIsOpen={garageModalIsOpen}
          closeGarageModal={handleCloseGarageModal}
          openGarageModal={handleOpenGarageModal}
          showCarousel={items.showCarousel}
          setCarId={setSelectedCarId}
          loading={items.isLoading}
          error={items.hasErrored}
        />

        {!items.isLoading && !items.hasErrored && items.total > 0 && (
          <Pagination />
        )}

        {items.internalLinks.length > 0 && (
          <InternalLinks
            internalLinks={items.internalLinks}
            title={`${I18n.t('more')} ${items.manufacturerTitle} ${I18n.t(
              'models'
            )}`}
          />
        )}

        {items.modelInternalLinks.length > 0 && (
          <InternalLinks
            internalLinks={items.modelInternalLinks}
            title={`${I18n.t('search.search')} ${items.modelTitle} ${I18n.t(
              'by_year'
            )}`}
          />
        )}

        {items.footerDescription &&
          (items.currentFilters.page === 1 || !items.currentFilters.page) && (
            <FooterDescription
              title={items.footerTitle}
              description={items.footerDescription}
              seoUpdatedAt={items.seoUpdatedAt}
            />
          )}

        {items.videoUrl &&
          (items.currentFilters.page === 1 || !items.currentFilters.page) && (
            <EmbeddedVideo videoUrl={items.videoUrl} />
          )}

        {items.faq && <FaqContainer description={items.faq} />}
      </div>

      <Suspense fallback={<i className="fas fa-2x fa-spinner-third fa-spin" />}>
        {garageModalIsOpen && (
          <AddToGarageModal
            modalIsOpen={garageModalIsOpen}
            closeGarageModal={handleCloseGarageModal}
            openGarageModal={handleOpenGarageModal}
            carId={selectedCarId}
          />
        )}
      </Suspense>
    </>
  )
}

export default SearchResult
